import type { AxiosError } from 'axios'
import type { TFunction } from 'i18next'
import type { AuthRole } from '@/shared/constants'
import { Eye, EyeOff, Loader2 } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useLoginMutation } from '@/entities/session'
import {
  clearRememberMeCredential,
  saveRememberMeCredential,
  useRememberMe,
} from '@/features/auth/hooks/useRemberMe'
import { ADMIN_ROLES } from '@/shared/constants'
import { useAuthSession } from '@/shared/hooks/use-auth-session'
import { getApiErrorText } from '@/shared/libs/get-api-error-text'
import { Button } from '@/shared/ui/button'
import { Input } from '@/shared/ui/input'
import { Label } from '@/shared/ui/label'

interface LoginFormProps {
  onSuccess: () => void
  tenantSlug?: string | null
}

function getLoginErrorMessage(error: unknown, t: TFunction): string {
  const fallback = t('auth.loginFailed')
  const apiText = getApiErrorText(error)
  if (apiText) return apiText

  if (typeof error !== 'object' || !error) return fallback

  const status = (error as AxiosError).response?.status
  if (status === 401) return t('auth.unauthorized')
  if (status === 404) return t('auth.tenantNotFound')
  if (status === 429) return t('auth.tooManyRequests')

  return fallback
}

export function LoginForm({ onSuccess, tenantSlug }: LoginFormProps) {
  const { t } = useTranslation()
  const [account, setAccount] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const accountInputRef = useRef<HTMLInputElement | null>(null)
  const passwordInputRef = useRef<HTMLInputElement | null>(null)
  const { login: loginAction, rememberMe, setRememberMe } = useAuthSession()
  const loginMutation = useLoginMutation()
  const { infoAccount } = useRememberMe()

  useEffect(() => {
    if (!infoAccount) {
      return
    }
    queueMicrotask(() => {
      setAccount((prev) => prev || infoAccount.account)
      setPassword((prev) => prev || infoAccount.password)
    })
  }, [infoAccount])

  function onRememberMeChange() {
    const nextRememberMe = !rememberMe
    void setRememberMe(nextRememberMe)
    if (!nextRememberMe) {
      void clearRememberMeCredential()
    }
  }

  async function onFormSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)

    const accountValue = accountInputRef.current?.value ?? account
    const passwordValue = passwordInputRef.current?.value ?? password
    const trimmedAccount = accountValue.trim()
    if (!trimmedAccount || !passwordValue) {
      setError(t('common.required'))
      return
    }

    try {
      const response = await loginMutation.mutateAsync({
        account: trimmedAccount,
        password: passwordValue,
        tenantSlug: tenantSlug ?? null,
      })

      if (response.user.role && !ADMIN_ROLES.includes(response.user.role as AuthRole)) {
        setError(t('auth.adminOnly'))
        return
      }

      loginAction(response.user, response.token, rememberMe, {
        defaultTenantId: response.defaultTenantId ?? null,
        languageCode: response.languageCode ?? null,
        tenantSlug: tenantSlug ?? null,
      })

      const refreshTokenStorage = rememberMe ? localStorage : sessionStorage
      const otherStorage = rememberMe ? sessionStorage : localStorage
      refreshTokenStorage.setItem('refresh_token', response.refreshToken)
      otherStorage.removeItem('refresh_token')
      if (rememberMe) {
        void saveRememberMeCredential(trimmedAccount, passwordValue)
      } else {
        void clearRememberMeCredential()
      }

      onSuccess()
    } catch (submitError) {
      setError(getLoginErrorMessage(submitError, t))
    }
  }

  return (
    <form onSubmit={onFormSubmit} className="space-y-4">
      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      <div className="space-y-1.5">
        <Label htmlFor="account">{t('auth.account')}</Label>
        <Input
          ref={accountInputRef}
          id="account"
          type="text"
          autoComplete="username"
          placeholder={t('auth.account')}
          value={account}
          onChange={(e) => setAccount(e.target.value)}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="password">{t('auth.password')}</Label>
        <div className="relative">
          <Input
            ref={passwordInputRef}
            id="password"
            type={showPassword ? 'text' : 'password'}
            autoComplete="current-password"
            placeholder={t('auth.password')}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="pr-10"
          />
          <button
            type="button"
            onClick={() => setShowPassword((prev) => !prev)}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600 transition-colors"
            tabIndex={-1}
            aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
          >
            {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <input
          id="rememberMe"
          type="checkbox"
          checked={rememberMe}
          onChange={onRememberMeChange}
          className="h-4 w-4 rounded border-zinc-300 text-blue-600 accent-blue-600 cursor-pointer"
        />
        <label htmlFor="rememberMe" className="text-sm text-zinc-600 cursor-pointer select-none">
          {t('auth.rememberMe')}
        </label>
      </div>

      <Button
        type="submit"
        className="w-full bg-blue-600 hover:bg-blue-700"
        disabled={loginMutation.isPending}
      >
        {loginMutation.isPending && <Loader2 className="animate-spin" />}
        {t('auth.login')}
      </Button>
    </form>
  )
}
